import { useCallback, useEffect, useMemo, useState } from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import { SalonIcon } from "./SalonIcon";
import { ActionButton } from "../adaptive/actions";
import type {
  SalonConversationDetail,
  SalonConversationLifecycle,
  SalonConversationOperatorState,
  SalonConversationPriority,
  SalonConversationWorkspaceConfiguration,
  SalonCustomer360,
  SalonInbox,
  SalonInboxBooking,
  SalonInboxItem,
  SalonInboxScope,
  SalonInboxView,
} from "./types";

const INBOX_SCOPES: ReadonlyArray<{ key: SalonInboxScope; label: string }> = Object.freeze([
  { key: "mine", label: "Mías" },
  { key: "unassigned", label: "Sin asignar" },
  { key: "all", label: "Todas" },
]);

const INBOX_VIEWS: ReadonlyArray<{ key: SalonInboxView; label: string }> = Object.freeze([
  { key: "attention", label: "Para revisar" },
  { key: "open", label: "Abiertas" },
  { key: "ai", label: "IA" },
  { key: "booking", label: "Reservas" },
  { key: "closed", label: "Cerradas" },
  { key: "all", label: "Todas" },
]);

const PRIORITIES: ReadonlyArray<{ key: "all" | SalonConversationPriority; label: string }> = Object.freeze([
  { key: "all", label: "Todas las prioridades" },
  { key: "urgent", label: "Urgente" },
  { key: "high", label: "Alta" },
  { key: "medium", label: "Media" },
  { key: "low", label: "Baja" },
]);

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudo completar la operación.";
}

function channelLabel(channel: string): string {
  switch (channel.toLowerCase()) {
    case "whatsapp": return "WhatsApp";
    case "voice": return "Voice";
    case "instagram": return "Instagram";
    case "messenger":
    case "facebook": return "Messenger";
    case "webchat": return "Webchat";
    default: return channel;
  }
}

function channelIcon(channel: string): string {
  switch (channel.toLowerCase()) {
    case "instagram": return "photo_camera";
    case "voice": return "call";
    case "webchat": return "forum";
    default: return "chat";
  }
}

function relativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "—";
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? "Ayer" : `hace ${days} d`;
}

function dateTime(value: string | null | undefined): string {
  if (!value || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function initials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.length
    ? parts.map((part) => part[0]?.toUpperCase() ?? "").join("")
    : "?";
}

function lifecycleLabel(value: SalonConversationLifecycle): string {
  switch (value) {
    case "pending": return "Pendiente";
    case "snoozed": return "Pospuesta";
    case "resolved": return "Resuelta";
    default: return "Abierta";
  }
}

function priorityLabel(value: SalonConversationPriority): string {
  switch (value) {
    case "urgent": return "Urgente";
    case "high": return "Alta";
    case "low": return "Baja";
    default: return "Media";
  }
}

function deliveryLabel(value: string): string {
  switch (value) {
    case "sending": return "Enviando";
    case "sent": return "Enviado";
    case "delivered": return "Entregado";
    case "read": return "Leído";
    case "failed": return "Falló";
    case "in_doubt": return "Estado incierto";
    default: return value;
  }
}

function operatorEventLabel(type: string): string {
  switch (type) {
    case "conversation.lifecycle.changed": return "Estado de conversación actualizado";
    case "conversation.priority.changed": return "Prioridad actualizada";
    case "conversation.assignment.changed": return "Asignación actualizada";
    case "conversation.control.changed": return "Control IA/Humano actualizado";
    case "conversation.labels.changed": return "Etiquetas actualizadas";
    case "conversation.private-note.added": return "Nota privada agregada";
    default: return "Actividad de conversación";
  }
}

function bookingPhaseLabel(phase: string): string {
  switch (phase) {
    case "idle": return "Sin reserva activa";
    case "collecting": return "Recolectando datos";
    case "review": return "Revisión de reserva";
    case "awaiting_confirmation": return "Esperando confirmación";
    case "committed": return "Turno confirmado";
    case "cancelled": return "Reserva cancelada";
    default: return "Reserva en curso";
  }
}

function BookingState({ booking }: { booking: SalonInboxBooking }) {
  return <div className={`cd-inbox-event${booking.phase === "committed" ? " cd-inbox-event--success" : ""}`}>
    <SalonIcon name={booking.phase === "committed" ? "event_available" : "event_upcoming"} className="cd-inbox-event__icon" />
    <div>
      <p className="cd-inbox-event__title">{bookingPhaseLabel(booking.phase)}</p>
      <p className="cd-inbox-event__body">
        {booking.serviceName ?? "Servicio por definir"}
        {booking.selectedStartsAt ? ` · ${dateTime(booking.selectedStartsAt)}` : ""}
        {booking.candidateSlotsCount > 0 ? ` · ${booking.candidateSlotsCount} horarios candidatos` : ""}
      </p>
    </div>
  </div>;
}

function InboxRow({
  item,
  selected,
  onSelect,
}: {
  item: SalonInboxItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const badges: Array<{ key: string; label: string; className: string }> = [];
  if (item.operator.controlMode === "ai" && item.operator.lifecycle !== "resolved") {
    badges.push({ key: "ai", label: "IA Activa", className: "cd-inbox-badge--ai" });
  }
  if (item.operator.controlMode === "human" && item.operator.lifecycle !== "resolved") {
    badges.push({ key: "human", label: "Esperando Humano", className: "cd-inbox-badge--human" });
  }
  if (item.attention.needsReview) {
    badges.push({ key: "review", label: "Revisar", className: "cd-inbox-badge--urgent" });
  }
  if (item.operator.priority === "urgent" || item.operator.priority === "high") {
    badges.push({
      key: "priority",
      label: priorityLabel(item.operator.priority),
      className: item.operator.priority === "urgent" ? "cd-inbox-badge--urgent" : "cd-inbox-badge--booking",
    });
  }
  if (item.booking) badges.push({ key: "booking", label: "Reserva", className: "cd-inbox-badge--booking" });
  if (item.operator.lifecycle === "resolved") {
    badges.push({ key: "closed", label: "Resuelta", className: "cd-inbox-badge--closed" });
  }

  return <button
    type="button"
    className={`cd-inbox-row${item.attention.needsReview ? " cd-inbox-row--urgent" : ""}${selected ? " cd-inbox-row--selected" : ""}`}
    role="option"
    aria-selected={selected}
    onClick={onSelect}
  >
    <span className="cd-inbox-row__avatar" aria-hidden="true">{initials(item.customerName)}</span>
    <span className="cd-inbox-row__body">
      <span className="cd-inbox-row__top">
        <span className="cd-inbox-row__name">{item.customerName}</span>
        <span className="cd-inbox-row__time">{relativeTime(item.latestMessage?.occurredAt ?? item.updatedAt)}</span>
      </span>
      <span className="cd-inbox-row__meta">
        <span className="cd-inbox-row__channel">{item.customerPhone ?? channelLabel(item.channel)}</span>
        {badges.slice(0, 3).map((badge) =>
          <span key={badge.key} className={`cd-inbox-badge ${badge.className}`}>{badge.label}</span>)}
      </span>
      <span className="cd-inbox-row__preview">{item.latestMessage?.body ?? "Sin mensajes"}</span>
      {item.operator.labels.length > 0 &&
        <span className="cd-inbox-row__labels">{item.operator.labels.slice(0, 2).join(" · ")}</span>}
    </span>
  </button>;
}

type OperatorMutation = Parameters<PlatformApiClient["mutateSalonConversation"]>[2];

function ConversationWindow({
  item,
  detail,
  workspace,
  loading,
  error,
  busy,
  actionError,
  onBack,
  onToggleContact,
  contactOpen,
  onMutate,
  onReply,
  onNote,
  onCreateCanned,
}: {
  item: SalonInboxItem | null;
  detail: SalonConversationDetail | null;
  workspace: SalonConversationWorkspaceConfiguration | null;
  loading: boolean;
  error: string | null;
  busy: string | null;
  actionError: string | null;
  onBack: () => void;
  onToggleContact: () => void;
  contactOpen: boolean;
  onMutate: (input: OperatorMutation) => Promise<void>;
  onReply: (body: string) => Promise<void>;
  onNote: (body: string) => Promise<void>;
  onCreateCanned: (input: { title: string; shortcut: string; body: string }) => Promise<void>;
}) {
  const [composerMode, setComposerMode] = useState<"reply" | "note">("reply");
  const [text, setText] = useState("");
  const [savingSnippet, setSavingSnippet] = useState(false);
  const [snippetTitle, setSnippetTitle] = useState("");
  const [snippetShortcut, setSnippetShortcut] = useState("");

  useEffect(() => {
    setText("");
    setSavingSnippet(false);
    setSnippetTitle("");
    setSnippetShortcut("");
  }, [item?.id]);

  const timeline = useMemo(() => {
    if (!detail) return [];
    const messageItems = detail.messages.map((message) => ({
      key: `message:${message.id}`,
      kind: "message" as const,
      occurredAt: message.occurredAt,
      message,
    }));
    const noteItems = detail.privateNotes.map((note) => ({
      key: `note:${note.id}`,
      kind: "note" as const,
      occurredAt: note.createdAt,
      note,
    }));
    const eventItems = detail.events
      .filter((event) => event.type !== "conversation.private-note.added")
      .map((event) => ({
        key: `event:${event.id}`,
        kind: "event" as const,
        occurredAt: event.occurredAt,
        event,
      }));
    const deliveryItems = detail.outbound
      .filter((receipt) => !receipt.messageId && ["failed", "in_doubt"].includes(receipt.status))
      .map((receipt) => ({
        key: `delivery:${receipt.id}`,
        kind: "delivery" as const,
        occurredAt: receipt.updatedAt,
        receipt,
      }));
    const bookingItems = detail.state?.booking
      ? [{
          key: "booking:current",
          kind: "booking" as const,
          occurredAt: detail.state.updatedAt,
          booking: detail.state.booking,
        }]
      : [];
    return [...messageItems, ...noteItems, ...eventItems, ...deliveryItems, ...bookingItems]
      .sort((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt));
  }, [detail]);

  if (!item) {
    return <div className="cd-inbox-conversation-empty">
      <SalonIcon name="forum" className="cd-inbox-conversation-empty__icon" />
      <p className="cd-inbox-conversation-empty__title">Seleccioná una conversación</p>
      <p className="cd-inbox-conversation-empty__hint">Elegí un cliente de la lista para ver sus mensajes.</p>
    </div>;
  }

  const operator = detail?.operator ?? item.operator;
  const canReply =
    Boolean(detail?.limits.replyFromInbox) &&
    Boolean(workspace?.capabilities.replyChannels.includes(item.channel.toLowerCase()));
  const isHuman = operator.controlMode === "human";

  async function submit() {
    const body = text.trim();
    if (!body) return;
    if (composerMode === "note") await onNote(body);
    else await onReply(body);
    setText("");
  }

  return <div className="cd-inbox-conv">
    <header className="cd-inbox-conv__header">
      <button type="button" className="cd-inbox-back-btn" aria-label="Volver a la lista" onClick={onBack}>
        <SalonIcon name="arrow_back" />
      </button>
      <button type="button" className="cd-inbox-conv__contact-trigger" onClick={onToggleContact} aria-label={`${contactOpen ? "Ocultar" : "Mostrar"} contexto de ${item.customerName}`} aria-expanded={contactOpen}>
        <span className="cd-inbox-conv__avatar" aria-hidden="true">{initials(item.customerName)}</span>
        <span className="cd-inbox-conv__identity">
          <span className="cd-inbox-conv__title-row">
            <span className="cd-inbox-conv__name">{item.customerName}</span>
            <span className="cd-inbox-badge cd-inbox-badge--status">{lifecycleLabel(operator.lifecycle)}</span>
          </span>
          <span className="cd-inbox-conv__channel">
            <SalonIcon name={channelIcon(item.channel)} />
            {channelLabel(item.channel)} · {operator.controlMode === "ai" ? "Modo IA" : "Control humano"}
          </span>
        </span>
      </button>
      <div className="cd-inbox-conv__actions">
        <span className={`cd-inbox-agent-status cd-inbox-agent-status--${operator.controlMode}`} role="status">
          {operator.controlMode === "ai" ? "Modo IA Activo" : "Control humano activo"}
        </span>
        <button
          type="button"
          className={`cd-inbox-control-pill cd-inbox-control-pill--${operator.controlMode}`}
          disabled={Boolean(busy)}
          onClick={() => void onMutate({
            type: "control",
            controlMode: operator.controlMode === "ai" ? "human" : "ai",
            expectedRevision: operator.revision,
          })}
        >
          <SalonIcon name={operator.controlMode === "ai" ? "smart_toy" : "person"} />
          {operator.controlMode === "ai" ? "Tomar control humano" : "Cambiar a modo IA"}
        </button>
        <button type="button" className="cd-inbox-action-btn cd-inbox-action-btn--icon" onClick={onToggleContact} aria-label={contactOpen ? "Ocultar contexto operativo" : "Mostrar contexto operativo"} aria-expanded={contactOpen}>
          <SalonIcon name="tune" />
        </button>
      </div>
    </header>

    {item.attention.needsReview && <div className="cd-inbox-conv-banner" role="status">
      <SalonIcon name="warning" className="cd-inbox-conv-banner__icon" />
      <div className="cd-inbox-conv-banner__text">
        <p className="cd-inbox-conv-banner__title">Requiere atención</p>
        <p className="cd-inbox-conv-banner__body">
          {item.attention.reason === "human_mode"
            ? "La conversación está bajo control humano."
            : "El último mensaje registrado es del cliente."}
        </p>
      </div>
    </div>}

    <div className="cd-inbox-conv__timeline">
      <div className="cd-inbox-timeline__day"><span>Actividad de la conversación</span></div>
      {loading && <p className="cd-inbox-timeline__empty">Cargando historial…</p>}
      {error && <p className="cd-inbox-timeline__empty">{error}</p>}
      {!loading && !error && detail && <>
        {timeline.map((entry) => {
          if (entry.kind === "message") {
            const receipt = detail.outbound.find((candidate) => candidate.messageId === entry.message.id);
            const voice = detail.thread.channel === "voice";
            return <div
              key={entry.key}
              className={`cd-inbox-msg ${entry.message.direction === "outbound" ? "cd-inbox-msg--out" : "cd-inbox-msg--in"}`}
            >
              <span className="cd-inbox-msg__label">
                {entry.message.direction === "outbound"
                  ? <span className="cd-inbox-msg__role-tag">
                      {entry.message.sender && entry.message.sender !== "agent" ? "[Operador]" : "[IA]"}
                    </span>
                  : item.customerName}
                {voice ? " · Transcripción Voice" : ""} · {dateTime(entry.message.occurredAt)}
              </span>
              <div className="cd-inbox-msg__bubble">{entry.message.body}</div>
              {receipt && <span className={`cd-inbox-delivery cd-inbox-delivery--${receipt.status}`}>
                {deliveryLabel(receipt.status)}
              </span>}
            </div>;
          }
          if (entry.kind === "note") {
            return <div key={entry.key} className="cd-inbox-note">
              <SalonIcon name="lock" />
              <div>
                <strong>Nota privada</strong>
                <p>{entry.note.body}</p>
                <span>{dateTime(entry.note.createdAt)}</span>
              </div>
            </div>;
          }
          if (entry.kind === "booking") {
            return <BookingState key={entry.key} booking={entry.booking} />;
          }
          if (entry.kind === "delivery") {
            return <div key={entry.key} className="cd-inbox-event cd-inbox-event--danger">
              <SalonIcon name="error" className="cd-inbox-event__icon" />
              <div>
                <p className="cd-inbox-event__title">{deliveryLabel(entry.receipt.status)}</p>
                <p className="cd-inbox-event__body">{entry.receipt.body}</p>
                {entry.receipt.status === "failed"
                  ? <button className="cd-btn-ghost" type="button" onClick={() => setText(entry.receipt.body)}>Preparar reintento seguro</button>
                  : <p className="cd-inbox-event__body">No se reintentará automáticamente para evitar mensajes duplicados.</p>}
              </div>
            </div>;
          }
          return <div key={entry.key} className="cd-inbox-event">
            <SalonIcon name="history" className="cd-inbox-event__icon" />
            <div>
              <p className="cd-inbox-event__title">{operatorEventLabel(entry.event.type)}</p>
              <p className="cd-inbox-event__body">{dateTime(entry.event.occurredAt)}</p>
            </div>
          </div>;
        })}
        {timeline.length === 0 && <p className="cd-inbox-timeline__empty">Todavía no hay actividad registrada.</p>}
      </>}
    </div>

    <div className="cd-inbox-composer" aria-label="Compositor de conversación">
      <div className="cd-inbox-composer__tabs" role="tablist" aria-label="Modo del compositor">
        <button
          type="button"
          className={`cd-inbox-composer__tab${composerMode === "reply" ? " cd-inbox-composer__tab--active" : ""}`}
          role="tab"
          aria-selected={composerMode === "reply"}
          onClick={() => setComposerMode("reply")}
        >Responder</button>
        <button
          type="button"
          className={`cd-inbox-composer__tab${composerMode === "note" ? " cd-inbox-composer__tab--active" : ""}`}
          role="tab"
          aria-selected={composerMode === "note"}
          onClick={() => setComposerMode("note")}
        >Nota privada</button>
      </div>
      <div className="cd-inbox-composer__panel">
        {composerMode === "reply" && workspace && workspace.cannedResponses.length > 0 &&
          <select
            className="cd-inbox-composer__snippet"
            value=""
            onChange={(event) => {
              const canned = workspace.cannedResponses.find((item) => item.id === event.target.value);
              if (canned) setText(canned.body);
            }}
            aria-label="Insertar respuesta rápida"
          >
            <option value="">Respuesta rápida…</option>
            {workspace.cannedResponses.map((canned) =>
              <option key={canned.id} value={canned.id}>{canned.shortcut} · {canned.title}</option>)}
          </select>}
        <div className="cd-inbox-composer__row">
          <textarea
            className="cd-inbox-composer__input"
            rows={2}
            placeholder={composerMode === "note"
              ? "Escribí una nota visible solo para el equipo…"
              : isHuman ? "Escribí una respuesta…" : "Tomá control humano para responder…"}
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={Boolean(busy) || (composerMode === "reply" && (!canReply || !isHuman))}
          />
          <button
            type="button"
            className="cd-inbox-composer__send"
            disabled={Boolean(busy) || !text.trim() || (composerMode === "reply" && (!canReply || !isHuman))}
            onClick={() => void submit()}
            aria-label={composerMode === "note" ? "Guardar nota privada" : "Enviar respuesta"}
          >
            <SalonIcon name={composerMode === "note" ? "lock" : "send"} />
          </button>
        </div>
        {composerMode === "reply" && !isHuman &&
          <p className="cd-inbox-composer__notice cd-inbox-composer__notice--ai" role="status">
            La IA está respondiendo automáticamente. Haz clic en "Tomar control humano" para responder manualmente.
          </p>}
        {composerMode === "reply" && !isHuman &&
          <button
            type="button"
            className="cd-inbox-composer__takeover"
            disabled={Boolean(busy)}
            onClick={() => void onMutate({
              type: "control",
              controlMode: "human",
              expectedRevision: operator.revision,
            })}
          >
            <SalonIcon name="person" /> Tomar control humano
          </button>}
        {composerMode === "reply" && isHuman && !canReply &&
          <p className="cd-inbox-composer__notice"><SalonIcon name="info" />El canal actual no tiene transporte outbound habilitado.</p>}
        {workspace && !workspace.capabilities.attachments &&
          <p className="cd-inbox-composer__notice"><SalonIcon name="info" />Archivos adjuntos todavía no están habilitados para este canal.</p>}
        {actionError && <p className="cd-inbox-composer__error" role="alert">{actionError}</p>}
        {composerMode === "reply" && text.trim() && !savingSnippet &&
          <button type="button" className="cd-inbox-save-snippet" onClick={() => setSavingSnippet(true)}>
            Guardar como respuesta rápida
          </button>}
        {savingSnippet && <div className="cd-inbox-snippet-form">
          <input value={snippetTitle} onChange={(event) => setSnippetTitle(event.target.value)} placeholder="Nombre" aria-label="Nombre de respuesta rápida" />
          <input value={snippetShortcut} onChange={(event) => setSnippetShortcut(event.target.value)} placeholder="/atajo" aria-label="Atajo de respuesta rápida" />
          <button
            type="button"
            disabled={!snippetTitle.trim() || !snippetShortcut.trim() || Boolean(busy)}
            onClick={() => void onCreateCanned({
              title: snippetTitle.trim(),
              shortcut: snippetShortcut.trim(),
              body: text.trim(),
            }).then(() => {
              setSavingSnippet(false);
              setSnippetTitle("");
              setSnippetShortcut("");
            }).catch(() => undefined)}
          >Guardar</button>
          <button type="button" onClick={() => setSavingSnippet(false)}>Cancelar</button>
        </div>}
      </div>
    </div>
  </div>;
}

function ContactPanel({
  detail,
  customer360,
  workspace,
  collapsed,
  busy,
  onCollapse,
  onMutate,
  onCancelAppointment,
  navigate,
  basePath,
}: {
  detail: SalonConversationDetail | null;
  customer360: SalonCustomer360 | null;
  workspace: SalonConversationWorkspaceConfiguration | null;
  collapsed: boolean;
  busy: string | null;
  onCollapse: () => void;
  onMutate: (input: OperatorMutation) => Promise<void>;
  onCancelAppointment: (appointmentId: string) => Promise<void>;
  navigate: (path: string) => void;
  basePath: string;
}) {
  const customer = detail?.customer ?? null;
  const operator = detail?.operator ?? null;
  const nextAppointment = customer?.nextAppointment ?? null;
  const [labelDraft, setLabelDraft] = useState("");

  useEffect(() => setLabelDraft(""), [detail?.thread.id]);

  return <aside className={`cd-inbox-contact-panel${collapsed ? " cd-inbox-contact-panel--collapsed" : ""}`} aria-label="Contexto operativo">
    <div className="cd-inbox-contact-panel__head">
      <p className="cd-inbox-contact-panel__title">Contexto operativo</p>
      <button
        type="button"
        className="cd-inbox-contact-panel__toggle"
        aria-label={collapsed ? "Expandir contexto" : "Minimizar contexto"}
        aria-expanded={!collapsed}
        onClick={onCollapse}
      >
        <SalonIcon name={collapsed ? "chevron_left" : "chevron_right"} />
      </button>
    </div>
    {!collapsed && <div className="cd-inbox-contact-panel__body">
      {!customer
        ? <p className="cd-inbox-contact-empty">Sin cliente canónico vinculado.</p>
        : <>
            <div className="cd-inbox-contact-identity">
              <span className="cd-inbox-row__avatar" aria-hidden="true">{initials(customer.displayName)}</span>
              <div><strong>{customer.displayName}</strong><span>{customer.phone ?? "Sin teléfono"}</span></div>
            </div>
            <dl className="cd-inbox-contact-data">
              <div><dt>Email</dt><dd>{customer.email ?? "—"}</dd></div>
              <div><dt>Origen</dt><dd>{customer.sourceChannel ?? "—"}</dd></div>
              <div><dt>Última actividad</dt><dd>{dateTime(customer.lastSeenAt)}</dd></div>
            </dl>
            <button type="button" className="cd-inbox-context-link" onClick={() => navigate(`${basePath}/customers/${customer.id}`)}>
              Abrir Customer 360
            </button>
          </>}

      {operator && <section className="cd-inbox-ops-card" aria-label="Operación de conversación">
        <div className="cd-inbox-ops-card__head"><strong>Operación</strong><span>rev. {operator.revision}</span></div>
        <label>Estado
          <select
            value={operator.lifecycle}
            disabled={Boolean(busy)}
            onChange={(event) => void onMutate({
              type: "lifecycle",
              lifecycle: event.target.value as SalonConversationLifecycle,
              expectedRevision: operator.revision,
            })}
          >
            <option value="open">Abierta</option>
            <option value="pending">Pendiente</option>
            <option value="snoozed">Pospuesta</option>
            <option value="resolved">Resuelta</option>
          </select>
        </label>
        <label>Prioridad
          <select
            value={operator.priority}
            disabled={Boolean(busy)}
            onChange={(event) => void onMutate({
              type: "priority",
              priority: event.target.value as SalonConversationPriority,
              expectedRevision: operator.revision,
            })}
          >
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </label>
        <label>Asignación
          <select
            value={operator.assignedTo ?? ""}
            disabled={Boolean(busy)}
            onChange={(event) => void onMutate({
              type: "assignment",
              assignedTo: event.target.value || null,
              assignedTeamId: null,
              expectedRevision: operator.revision,
            })}
          >
            <option value="">Sin asignar</option>
            {workspace?.assignees.map((assignee) =>
              <option key={assignee.id} value={assignee.id}>{assignee.displayName}</option>)}
          </select>
        </label>
        <div className="cd-inbox-control-switch" aria-label="Control de conversación">
          <button
            type="button"
            className={operator.controlMode === "human" ? "is-active" : ""}
            disabled={Boolean(busy)}
            onClick={() => operator.controlMode !== "human" && void onMutate({
              type: "control",
              controlMode: "human",
              expectedRevision: operator.revision,
            })}
          >Humano</button>
          <button
            type="button"
            className={operator.controlMode === "ai" ? "is-active" : ""}
            disabled={Boolean(busy)}
            onClick={() => operator.controlMode !== "ai" && void onMutate({
              type: "control",
              controlMode: "ai",
              expectedRevision: operator.revision,
            })}
          >IA</button>
        </div>
        <div className="cd-inbox-labels">
          {operator.labels.map((label) =>
            <button
              key={label}
              type="button"
              title="Quitar etiqueta"
              disabled={Boolean(busy)}
              onClick={() => void onMutate({
                type: "labels",
                labels: operator.labels.filter((item) => item !== label),
                expectedRevision: operator.revision,
              })}
            >{label} ×</button>)}
        </div>
        <div className="cd-inbox-label-add">
          <input
            list="salon-conversation-labels"
            value={labelDraft}
            onChange={(event) => setLabelDraft(event.target.value)}
            placeholder="Agregar etiqueta"
            aria-label="Agregar etiqueta"
          />
          <datalist id="salon-conversation-labels">
            {workspace?.labels.map((item) => <option key={item.label} value={item.label} />)}
          </datalist>
          <button
            type="button"
            disabled={!labelDraft.trim() || Boolean(busy)}
            onClick={() => {
              const label = labelDraft.trim();
              if (!label || operator.labels.includes(label)) return;
              void onMutate({
                type: "labels",
                labels: [...operator.labels, label],
                expectedRevision: operator.revision,
              }).then(() => setLabelDraft(""));
            }}
          >+</button>
        </div>
      </section>}

      {nextAppointment
        ? <section className="cd-inbox-contact-booking">
            <SalonIcon name="event_available" />
            <div>
              <strong>Próximo turno</strong>
              <span>{nextAppointment.serviceName}</span>
              <span>{dateTime(nextAppointment.startsAt)}</span>
              <div className="cd-inbox-context-actions">
                <button type="button" onClick={() => navigate(`${basePath}/agenda?appointmentId=${encodeURIComponent(nextAppointment.id)}&customerId=${encodeURIComponent(customer!.id)}&startsAt=${encodeURIComponent(nextAppointment.startsAt)}`)}>Reprogramar</button>
                <button
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    if (window.confirm("¿Cancelar este turno? Esta acción actualizará Scheduling.")) {
                      void onCancelAppointment(nextAppointment.id);
                    }
                  }}
                >Cancelar</button>
              </div>
            </div>
          </section>
        : <button type="button" className="cd-inbox-context-primary" onClick={() => navigate(customer ? `${basePath}/agenda?customerId=${encodeURIComponent(customer.id)}` : `${basePath}/agenda`)}>
            <SalonIcon name="event_available" /> Crear turno en Agenda
          </button>}

      {customer360 && <section className="cd-inbox-ops-card">
        <div className="cd-inbox-ops-card__head"><strong>Historial y ventas</strong></div>
        {customer360.customer.notes && <p className="cd-inbox-customer-note">
          <strong>Nota del cliente</strong>
          <span>{customer360.customer.notes}</span>
        </p>}
        <p className="cd-inbox-context-stat">
          {customer360.summary.totalAppointments ?? 0} turnos registrados · {customer360.summary.noShowAppointments ?? 0} no-show
        </p>
        {customer360.salesByCurrency.length > 0
          ? customer360.salesByCurrency.map((sale) =>
              <div key={sale.currency} className="cd-inbox-money-row">
                <span>{sale.currency}</span>
                <strong>{(sale.collectedAmountMinor / 100).toLocaleString("es-AR")}</strong>
                <small>{(sale.pendingAmountMinor / 100).toLocaleString("es-AR")} pendiente</small>
              </div>)
          : <p className="cd-inbox-contact-empty">Sin ventas canónicas registradas.</p>}
        <div className="cd-inbox-history-list">
          {customer360.appointments.slice(-3).reverse().map((appointment) =>
            <div key={appointment.id}>
              <span>{dateTime(appointment.startsAt)}</span>
              <strong>{appointment.service?.name ?? "Servicio"}</strong>
              <small>{appointment.status}</small>
            </div>)}
        </div>
      </section>}
    </div>}
  </aside>;
}

export function SalonsInbox({
  organizationId,
  nestedPath,
  api,
  navigate,
  basePath,
}: {
  organizationId: string;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  basePath: string;
}) {
  const nestedParts = nestedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const routeConversationId =
    nestedParts[0] === "conversations" && nestedParts[1]
      ? nestedParts[1]
      : null;

  const [scope, setScope] = useState<SalonInboxScope>("all");
  const [view, setView] = useState<SalonInboxView>("open");
  const [priority, setPriority] = useState<"all" | SalonConversationPriority>("all");
  const [control, setControl] = useState<"all" | "ai" | "human">("all");
  const [channel, setChannel] = useState("all");
  const [label, setLabel] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [snapshot, setSnapshot] = useState<SalonInbox | null>(null);
  const [workspace, setWorkspace] = useState<SalonConversationWorkspaceConfiguration | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(routeConversationId);
  const [detail, setDetail] = useState<SalonConversationDetail | null>(null);
  const [customer360, setCustomer360] = useState<SalonCustomer360 | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [contactCollapsed, setContactCollapsed] = useState(false);
  const [mobileContactOpen, setMobileContactOpen] = useState(false);

  // Desktop shows the real Customer 360/Scheduling context; mobile remains a drawer.
  useEffect(() => { setContactCollapsed(window.matchMedia("(max-width: 1023px)").matches); }, [selectedId]);

  useEffect(() => {
    if (routeConversationId) {
      setSelectedId(routeConversationId);
      return;
    }
    if (window.matchMedia("(max-width: 1023px)").matches) {
      setSelectedId(null);
      setDetail(null);
    }
  }, [routeConversationId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchDraft.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const loadWorkspace = useCallback(async () => {
    const next = await api.salonConversationWorkspace(organizationId);
    setWorkspace(next);
  }, [api, organizationId]);

  const loadList = useCallback(async () => {
    const next = await api.salonInbox(organizationId, {
      q: query,
      view,
      scope,
      priority,
      control,
      channel,
      label,
      limit: 100,
    });
    setSnapshot(next);
    setSelectedId((current) => {
      if (routeConversationId) return routeConversationId;
      if (current && next.items.some((item) => item.id === current)) return current;
      return window.matchMedia("(min-width: 1024px)").matches
        ? next.items[0]?.id ?? null
        : null;
    });
  }, [api, organizationId, query, view, scope, priority, control, channel, label, routeConversationId]);

  const loadDetail = useCallback(async (conversationId: string) => {
    const next = await api.salonConversation(organizationId, conversationId);
    setDetail(next);
    if (next.customer) {
      try {
        setCustomer360(await api.salonCustomer360(organizationId, next.customer.id));
      } catch {
        setCustomer360(null);
      }
    } else {
      setCustomer360(null);
    }
  }, [api, organizationId]);

  useEffect(() => {
    let current = true;
    setLoadingList(true);
    setListError(null);
    Promise.all([loadList(), workspace ? Promise.resolve() : loadWorkspace()])
      .catch((error) => {
        if (!current) return;
        setSnapshot(null);
        setSelectedId(null);
        setListError(errorText(error));
      })
      .finally(() => { if (current) setLoadingList(false); });
    return () => { current = false; };
  }, [loadList, loadWorkspace, reloadNonce]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setCustomer360(null);
      setDetailError(null);
      return;
    }
    let current = true;
    setLoadingDetail(true);
    setDetailError(null);
    loadDetail(selectedId)
      .catch((error) => { if (current) setDetailError(errorText(error)); })
      .finally(() => { if (current) setLoadingDetail(false); });
    return () => { current = false; };
  }, [selectedId, loadDetail]);

  const selectedItem = useMemo(
    () => snapshot?.items.find((item) => item.id === selectedId) ?? null,
    [snapshot, selectedId],
  );

  async function refreshCurrent() {
    await loadList();
    if (selectedId) await loadDetail(selectedId);
  }

  async function mutate(input: OperatorMutation) {
    if (!selectedId) return;
    setBusy("mutation");
    setActionError(null);
    try {
      await api.mutateSalonConversation(organizationId, selectedId, input);
      if (input.type === "labels") await loadWorkspace();
      await refreshCurrent();
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  async function reply(body: string) {
    if (!selectedId) return;
    setBusy("reply");
    setActionError(null);
    try {
      await api.replySalonConversation(organizationId, selectedId, body);
      await refreshCurrent();
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  async function note(body: string) {
    if (!selectedId || !detail) return;
    setBusy("note");
    setActionError(null);
    try {
      await api.addSalonConversationPrivateNote(
        organizationId,
        selectedId,
        body,
        detail.operator.revision,
      );
      await refreshCurrent();
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  async function createCanned(input: { title: string; shortcut: string; body: string }) {
    setBusy("canned");
    setActionError(null);
    try {
      await api.createSalonConversationCannedResponse(organizationId, input);
      await loadWorkspace();
    } catch (error) {
      setActionError(errorText(error));
      throw error;
    } finally {
      setBusy(null);
    }
  }

  async function cancelAppointment(appointmentId: string) {
    setBusy("appointment");
    setActionError(null);
    try {
      await api.cancelSalonAgendaAppointment(organizationId, appointmentId);
      if (selectedId) await loadDetail(selectedId);
    } catch (error) {
      setActionError(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  const countLabel = snapshot
    ? [
        snapshot.counts.attention > 0 ? `${snapshot.counts.attention} para revisar` : null,
        `${snapshot.counts.open} abiertas`,
        snapshot.counts.booking > 0 ? `${snapshot.counts.booking} con reserva` : null,
      ].filter(Boolean).join(" · ")
    : "—";

  return <section
    className={`cd-inbox-product cd-inbox-product--canon${selectedId ? " cd-inbox-product--in-chat" : ""}`}
    aria-labelledby="salons-inbox-title"
  >
    <div className="cd-page-frame cd-inbox-page-frame">
      <header className="cd-page-header cd-inbox-product__page-header">
        <div>
          <p className="cd-page-header__eyebrow">Conversaciones / Operación</p>
          <h1 id="salons-inbox-title" className="cd-page-header__title">Conversaciones</h1>
          <p className="cd-page-header__description cd-inbox-product__desc-desktop">
            Atendé primero la conversación. Consultá la información del cliente cuando la necesites.
          </p>
        </div>
        <div className="cd-inbox-product__page-actions">
          {basePath.startsWith("/demos/") && <button
            type="button"
            className="cd-btn-primary"
            onClick={() => navigate(`${basePath}/conversations/simulator`)}
          >Simular conversación</button>}
          <button
            type="button"
            className="cd-btn-ghost"
            onClick={() => navigate(`${basePath}/customers?create=1&source=conversation`)}
          >
            <SalonIcon name="person" />
            Nuevo cliente
          </button>
        </div>
      </header>

      {listError && <div className="cd-inbox-state cd-inbox-state--error" role="alert">
        <p className="cd-inbox-state__title">No se pudieron cargar las conversaciones</p>
        <p className="cd-inbox-state__hint">{listError}</p>
        <ActionButton variant="secondary" onClick={() => setReloadNonce((value) => value + 1)}>Reintentar</ActionButton>
      </div>}

      <div className="cd-inbox-product__content cd-inbox-content">
        <div className={`cd-inbox-workspace${selectedId ? " cd-inbox-workspace--conversation" : ""}`} aria-live="polite">
          <div className="cd-inbox-list-col">
            <div className="cd-inbox-list-toolbar">
              <div className="cd-inbox-list-head"><p className="cd-inbox-list-head__title">Inbox</p></div>
              <div className="cd-inbox-search">
                <SalonIcon name="search" className="cd-inbox-search__icon" />
                <input
                  type="search"
                  className="cd-inbox-search__input"
                  placeholder="Buscar nombre, mensaje o etiqueta…"
                  autoComplete="off"
                  aria-label="Buscar conversación"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
              </div>

              <nav className="cd-inbox-tabs cd-inbox-tabs--scope" aria-label="Propiedad de cola">
                {INBOX_SCOPES.map((item) => <button
                  key={item.key}
                  type="button"
                  className={`cd-inbox-view${scope === item.key ? " cd-inbox-view--active" : ""}`}
                  aria-current={scope === item.key ? "true" : undefined}
                  onClick={() => setScope(item.key)}
                >
                  <span className="cd-inbox-view__label">{item.label}</span>
                  <span className="cd-inbox-view__count">{listError || loadingList ? "—" : snapshot?.ownershipCounts[item.key] ?? 0}</span>
                </button>)}
              </nav>

              <div className="cd-inbox-primary-filter">
                <label>
                  <span className="sr-only">Estado de la cola</span>
                  <select aria-label="Estado de la cola" value={view} onChange={(event) => setView(event.target.value as SalonInboxView)}>
                    {INBOX_VIEWS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                </label>
              </div>
              <details className="cd-ux-filter-disclosure cd-inbox-advanced-filters">
                <summary>
                  Más filtros{priority !== "all" || control !== "all" || channel !== "all" || label ? " · activos" : ""}
                </summary>
                <div className="cd-inbox-filter-grid">
                  <select aria-label="Prioridad" value={priority} onChange={(event) => setPriority(event.target.value as "all" | SalonConversationPriority)}>
                    {PRIORITIES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                  <select aria-label="Control" value={control} onChange={(event) => setControl(event.target.value as "all" | "ai" | "human")}>
                    <option value="all">IA + Humano</option>
                    <option value="human">Humano</option>
                    <option value="ai">IA</option>
                  </select>
                  <select aria-label="Canal" value={channel} onChange={(event) => setChannel(event.target.value)}>
                    <option value="all">Todos los canales</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="voice">Voice</option>
                    <option value="instagram">Instagram</option>
                    <option value="messenger">Messenger</option>
                  </select>
                  <select aria-label="Etiqueta" value={label} onChange={(event) => setLabel(event.target.value)}>
                    <option value="">Todas las etiquetas</option>
                    {workspace?.labels.map((item) => <option key={item.label} value={item.label}>{item.label} ({item.usageCount})</option>)}
                  </select>
                </div>
                <ActionButton
                  variant="ghost"
                  disabled={priority === "all" && control === "all" && channel === "all" && !label}
                  onClick={() => {
                    setPriority("all");
                    setControl("all");
                    setChannel("all");
                    setLabel("");
                  }}
                >Limpiar filtros avanzados</ActionButton>
              </details>
              <p className="cd-inbox-meta-line">{countLabel}</p>
            </div>

            <div className="cd-inbox-list" role="listbox" aria-label="Conversaciones">
              {loadingList && <p className="cd-inbox-list__empty-state__title">Cargando conversaciones…</p>}
              {!loadingList && snapshot?.items.map((item) => <InboxRow
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                onSelect={() => {
                  setSelectedId(item.id);
                  setMobileContactOpen(false);
                  setContactCollapsed(window.matchMedia("(max-width: 1023px)").matches);
                  setActionError(null);
                  navigate(basePath + "/conversations/" + encodeURIComponent(item.id));
                }}
              />)}
              {!loadingList && snapshot && snapshot.items.length === 0 && <div className="cd-inbox-list__empty-state">
                <SalonIcon name="forum" className="cd-inbox-list__empty-state__icon" />
                <p className="cd-inbox-list__empty-state__title">No hay conversaciones en esta vista.</p>
                <p className="cd-inbox-list__empty-state__hint">Probá otra cola, búsqueda o filtro.</p>
              </div>}
            </div>
          </div>

          <div className="cd-inbox-conversation-col">
            <div className="cd-inbox-conversation">
              <ConversationWindow
                item={selectedItem}
                detail={detail}
                workspace={workspace}
                loading={loadingDetail}
                error={detailError}
                busy={busy}
                actionError={actionError}
                onBack={() => {
                  setSelectedId(null);
                  setDetail(null);
                  setMobileContactOpen(false);
                  navigate(basePath + "/conversations");
                }}
                contactOpen={mobileContactOpen || !contactCollapsed}
                onToggleContact={() => {
                  if (window.matchMedia("(max-width: 1023px)").matches) setMobileContactOpen(true);
                  else setContactCollapsed((current) => !current);
                }}
                onMutate={mutate}
                onReply={reply}
                onNote={note}
                onCreateCanned={createCanned}
              />
            </div>
          </div>

          {!contactCollapsed && <ContactPanel
            detail={detail}
            customer360={customer360}
            workspace={workspace}
            collapsed={false}
            busy={busy}
            onCollapse={() => setContactCollapsed((current) => !current)}
            onMutate={mutate}
            onCancelAppointment={cancelAppointment}
            navigate={navigate}
            basePath={basePath}
          />}
        </div>
      </div>
    </div>

    {mobileContactOpen && <div className="cd-inbox-mobile-contact-layer">
      <button type="button" className="cd-inbox-mobile-contact-backdrop" aria-label="Cerrar contexto" onClick={() => setMobileContactOpen(false)} />
      <div className="cd-inbox-mobile-contact-drawer" role="dialog" aria-modal="true" aria-label="Contexto operativo">
        <button type="button" className="cd-inbox-mobile-contact-close" onClick={() => setMobileContactOpen(false)} aria-label="Cerrar">×</button>
        <ContactPanel
          detail={detail}
          customer360={customer360}
          workspace={workspace}
          collapsed={false}
          busy={busy}
          onCollapse={() => setMobileContactOpen(false)}
          onMutate={mutate}
          onCancelAppointment={cancelAppointment}
          navigate={navigate}
          basePath={basePath}
        />
      </div>
    </div>}
  </section>;
}
